/** Public view of canonical venue metadata. City/provider coordinates are never a venue pin. */
export interface PublicVenueLocation {
  unitName: string;
  address: string | null;
  coordinates: { latitude: number; longitude: number } | null;
}
const text = (value: unknown, max: number): string | null => {
  if (typeof value !== 'string') return null;
  const clean = value.trim().replace(/\s+/g, ' ');
  return clean && clean.length <= max && !/[\u0000-\u001f]/.test(clean) ? clean : null;
};
export const validVenueCoordinates = (value: any): value is { latitude: number; longitude: number } =>
  !!value && typeof value.latitude === 'number' && Number.isFinite(value.latitude) && Math.abs(value.latitude) <= 90 &&
  typeof value.longitude === 'number' && Number.isFinite(value.longitude) && Math.abs(value.longitude) <= 180;
export const projectPublicVenueLocation = (unit: any): PublicVenueLocation => {
  const location = unit?.canonicalLocation;
  const canonical = location?.schemaVersion === 1 && ['geoapify', 'open-meteo'].includes(location.provider);
  const address = canonical ? text(location.guestAddress, 320) : null;
  const point = canonical && validVenueCoordinates(location.venueCoordinates) ? location.venueCoordinates : null;
  return { unitName: text(unit?.name, 160) || 'MintLeaf', address,
    coordinates: point ? { latitude: point.latitude, longitude: point.longitude } : null };
};
export const normalizeMapsEmbedKey = (key: unknown): string | null => {
  if (key === null || key === '') return null;
  if (typeof key !== 'string' || !/^AIza[\w-]{35}$/.test(key.trim())) throw new Error('INVALID_MAPS_EMBED_KEY');
  return key.trim();
};
const destination = (location: PublicVenueLocation | null | undefined): string | null => {
  if (!location) return null;
  if (validVenueCoordinates(location.coordinates)) return `${location.coordinates.latitude},${location.coordinates.longitude}`;
  const address = text(location.address, 320);
  return address && !/^[a-z][a-z0-9+.-]*:/i.test(address) ? address : null;
};
export const buildGoogleMapsDirectionsUrl = (location: PublicVenueLocation | null | undefined): string | null => {
  const target = destination(location); if (!target) return null;
  const url = new URL('https://www.google.com/maps/dir/');
  url.searchParams.set('api', '1'); url.searchParams.set('destination', target);
  return url.toString();
};
export const buildGoogleMapsEmbedUrl = (location: PublicVenueLocation | null | undefined, key: unknown, locale: 'hu' | 'en' = 'hu'): string | null => {
  const target = destination(location); if (!target) return null;
  let safeKey: string | null; try { safeKey = normalizeMapsEmbedKey(key); } catch { return null; }
  if (!safeKey) return null;
  const url = new URL('https://www.google.com/maps/embed/v1/place');
  url.searchParams.set('key', safeKey); url.searchParams.set('q', target); url.searchParams.set('language', locale === 'en' ? 'en' : 'hu');
  return url.toString();
};
